import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Upload, CheckCircle2, FileText, Loader2, AlertTriangle, Shield,
  BarChart3, Sparkles, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function StatementUploadPage() {
  const { token: uploadToken } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState(null); // null | analyzing | analyzed | proposal_ready | error
  const [analysisSummary, setAnalysisSummary] = useState(null);
  const [proposalToken, setProposalToken] = useState(null);
  const pollRef = useRef(null);

  // Poll for analysis status after upload
  const startPolling = useCallback(() => {
    setAnalysisStatus('analyzing');
    let attempts = 0;
    const maxAttempts = 40; // ~2 minutes

    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const r = await fetch(`${API}/api/statements/quick-send/${uploadToken}/status`);
        if (!r.ok) return;
        const data = await r.json();

        if (data.status === 'proposal_ready') {
          setAnalysisStatus('proposal_ready');
          setAnalysisSummary(data.analysis_summary);
          setProposalToken(data.proposal_token);
          clearInterval(pollRef.current);
        } else if (data.status === 'analyzed') {
          setAnalysisStatus('analyzed');
          setAnalysisSummary(data.analysis_summary);
          clearInterval(pollRef.current);
        } else if (data.status === 'analysis_error') {
          setAnalysisStatus('done');
          clearInterval(pollRef.current);
        }
      } catch (err) {
        console.warn('[StatementUpload] Polling error (non-fatal):', err);
      }

      if (attempts >= maxAttempts) {
        setAnalysisStatus('done');
        clearInterval(pollRef.current);
      }
    }, 3000);
  }, [uploadToken]);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    fetch(`${API}/api/statements/quick-send/${uploadToken}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 410 ? 'This link has expired.' : 'Link not found.');
        return r.json();
      })
      .then(data => {
        setPageData(data);
        if (data.already_uploaded) {
          setUploaded(true);
          // Check current analysis status for returning visitors
          fetch(`${API}/api/statements/quick-send/${uploadToken}/status`)
            .then(r => r.ok ? r.json() : null)
            .then(st => {
              if (!st) return;
              if (st.status === 'proposal_ready') {
                setAnalysisStatus('proposal_ready');
                setAnalysisSummary(st.analysis_summary);
                setProposalToken(st.proposal_token);
              } else if (st.status === 'analyzed') {
                setAnalysisStatus('analyzed');
                setAnalysisSummary(st.analysis_summary);
              } else if (st.status === 'uploaded') {
                // Analysis still in progress
                startPolling();
              } else {
                setAnalysisStatus('done');
              }
            })
            .catch(() => {});
        }
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [uploadToken, startPolling]);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await fetch(`${API}/api/statements/quick-send/${uploadToken}/upload`, {
        method: 'POST',
        body: formData,
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || 'Upload failed');
      }
      setUploaded(true);
      startPolling();
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
  };

  const branding = pageData?.tenant_branding || {};
  const primaryColor = branding.primary_color || '#1a1f4e';
  const companyName = branding.company_name || 'ISOCNTRL';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error && !pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <h1 className="text-lg font-bold mb-2">Link Unavailable</h1>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (uploaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6" data-testid="upload-success-page">
        <div className="text-center max-w-md">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <Step done label="Uploaded" color={primaryColor} />
            <div className="w-8 h-px bg-gray-300" />
            <Step
              done={analysisStatus === 'analyzed' || analysisStatus === 'proposal_ready'}
              active={analysisStatus === 'analyzing'}
              label="Analyzing"
              color={primaryColor}
            />
            <div className="w-8 h-px bg-gray-300" />
            <Step
              done={analysisStatus === 'proposal_ready'}
              active={analysisStatus === 'analyzed'}
              label="Proposal"
              color={primaryColor}
            />
          </div>

          {/* Main Content */}
          {analysisStatus === 'analyzing' ? (
            <>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${primaryColor}12` }}>
                <Sparkles className="w-8 h-8 animate-pulse" style={{ color: primaryColor }} />
              </div>
              <h1 className="text-xl font-bold mb-2">Analyzing Your Statement</h1>
              <p className="text-sm text-gray-600">
                Our AI is reading your processing statement and identifying every fee, rate, and pricing detail for <strong>{pageData?.business_name}</strong>.
              </p>
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>This usually takes 15-30 seconds</span>
              </div>
            </>
          ) : analysisStatus === 'proposal_ready' || analysisStatus === 'analyzed' ? (
            <>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${primaryColor}12` }}>
                <BarChart3 className="w-8 h-8" style={{ color: primaryColor }} />
              </div>
              <h1 className="text-xl font-bold mb-2">Analysis Complete!</h1>
              <p className="text-sm text-gray-600 mb-4">
                We&apos;ve finished analyzing the statement for <strong>{pageData?.business_name}</strong>.
                {analysisSummary?.total_volume && (
                  <> We found <strong>${Number(analysisSummary.total_volume).toLocaleString()}</strong> in monthly volume
                  {analysisSummary.effective_rate && <> at an effective rate of <strong>{(analysisSummary.effective_rate * 100).toFixed(2)}%</strong></>}.
                  </>
                )}
              </p>
              {analysisSummary?.pricing_program && analysisSummary.pricing_program !== 'unknown' && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium mb-4" style={{ background: `${primaryColor}12`, color: primaryColor }}>
                  <BarChart3 className="w-3 h-3" />
                  Current Pricing: {formatPricingProgram(analysisSummary.pricing_program)}
                </div>
              )}
              <p className="text-sm text-gray-500">
                Your personalized cost savings analysis is being prepared. Your {companyName} representative will reach out shortly with your results.
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: `${primaryColor}15` }}>
                <CheckCircle2 className="w-8 h-8" style={{ color: primaryColor }} />
              </div>
              <h1 className="text-xl font-bold mb-2">Statement Uploaded!</h1>
              <p className="text-sm text-gray-600">
                Thank you! We&apos;ve received your processing statement for <strong>{pageData?.business_name}</strong>.
                Your free cost analysis will be ready shortly.
              </p>
            </>
          )}

          <p className="text-xs text-gray-400 mt-6">You can close this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="public-upload-page">
      {/* Header */}
      <div className="py-6 px-4 text-center border-b" style={{ borderColor: `${primaryColor}30` }}>
        {branding.logo_url ? (
          <img src={branding.logo_url} alt={branding.company_name} className="h-10 mx-auto mb-2" />
        ) : (
          <h2 className="text-lg font-bold" style={{ color: primaryColor }}>{companyName}</h2>
        )}
      </div>

      {/* Main */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">Free Processing Cost Analysis</h1>
            <p className="text-sm text-gray-600">
              Upload your most recent monthly processing statement and we&apos;ll prepare a free cost analysis for <strong>{pageData?.business_name}</strong>.
            </p>
          </div>

          <div className="rounded-2xl border bg-white p-6 space-y-4 shadow-sm">
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: `${primaryColor}10` }}>
                <FileText className="w-7 h-7" style={{ color: primaryColor }} />
              </div>
              <p className="text-sm font-medium">Upload Processing Statement</p>
              <p className="text-xs text-gray-500">PDF format, up to 20MB</p>
            </div>

            <Input
              type="file"
              accept=".pdf,application/pdf"
              onChange={e => setFile(e.target.files?.[0] || null)}
              className="text-sm"
              data-testid="public-file-input"
            />

            {file && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 text-sm">
                <FileText className="w-4 h-4 text-gray-500" />
                <span className="truncate">{file.name}</span>
                <span className="text-xs text-gray-400 ml-auto">{(file.size / 1024).toFixed(0)}KB</span>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-600" data-testid="upload-error">{error}</p>
            )}

            <Button
              className="w-full h-12 text-base font-semibold"
              style={{ background: primaryColor }}
              onClick={handleUpload}
              disabled={!file || uploading}
              data-testid="public-upload-btn"
            >
              {uploading ? (
                <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Uploading &amp; Analyzing...</>
              ) : (
                <><Upload className="w-5 h-5 mr-2" /> Upload Statement</>
              )}
            </Button>
          </div>

          <div className="flex items-center gap-2 justify-center text-xs text-gray-400">
            <Shield className="w-3.5 h-3.5" />
            <span>Your statement is kept confidential and encrypted</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({ done, active, label, color }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all"
        style={{
          background: done ? color : active ? `${color}20` : '#e5e7eb',
          color: done ? '#fff' : active ? color : '#9ca3af',
        }}
      >
        {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : active ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
      </div>
      <span className="text-xs font-medium" style={{ color: done || active ? color : '#9ca3af' }}>{label}</span>
    </div>
  );
}

function formatPricingProgram(program) {
  const map = {
    interchange_plus: 'Interchange Plus',
    flat_rate: 'Flat Rate',
    err: 'ERR (Enhanced Recovery)',
    dual_pricing: 'Dual Pricing',
    tiered_3: '3-Tier Pricing',
    tiered_2: '2-Tier Pricing',
    tiered_4: '4-Tier Pricing',
  };
  return map[program] || program;
}
